import BillingPageLayout from "./BillingPageLayout";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { useTranslation } from "../../context/Internationalization";
import useGraphql from "../../Hooks/useGraphql";
import { GET_CLUB_INVOICE_DETAILS } from "../../graphql/billingGraphql";
import { BILLING_ROUTE, MANAGER_HOME_ROUTE } from "../../routes";
import StatusAlert from "../../Components/Common/StatusAlert";
import { Button, buttonVariants } from "../../shadcn/components/ui/button";
import BillingTaxIdentityForm from "./BillingTaxIdentityForm";
import { Card, CardContent, CardDescription, CardHeader } from "../../shadcn/components/ui/card";

function useInvoiceDetails(clubId, userId) {
  const { axiosGraphQL } = useGraphql();
  const callback = useRef(axiosGraphQL);
  callback.current = axiosGraphQL;
  const [refresh, setRefresh] = useState(0);
  const [state, setState] = useState(null);
  const invalidation = useRef(0);
  const requests = useRef(new Map());
  const controllers = useRef(new Set());
  const currentState = useRef(state);
  currentState.current = state;
  const scope = `${userId}:${clubId}`;
  useEffect(() => {
    const activeControllers = controllers.current;
    return () => {
      for (const controller of activeControllers) controller.abort();
      activeControllers.clear();
    };
  }, [scope]);
  useEffect(() => {
    if (!clubId || !userId) return;
    let active = true, pending = false;
    let hasRows = currentState.current?.scope === scope && currentState.current.rows.length > 0;
    let timer, delay = 30000;
    const load = async () => {
      if (pending) return;
      pending = true;
      // Effect restarts must wait for the previous request in this scope.
      const previousRequest = requests.current.get(scope);
      if (previousRequest) await previousRequest.catch(() => {});
      if (!active) return;
      const version = invalidation.current;
      const controller = new AbortController();
      controllers.current.add(controller);
      let deadline, cancelRequest;
      const transport = Promise.resolve().then(() => callback.current({
        query: GET_CLUB_INVOICE_DETAILS, variables: { clubId },
      }, { signal: controller.signal }));
      const request = Promise.race([transport, new Promise((_, reject) => {
        cancelRequest = () => reject(new Error("invoice_details_cancelled"));
        controller.signal.addEventListener("abort", cancelRequest, { once: true });
        deadline = setTimeout(() => {
          controller.abort();
          reject(new Error("invoice_details_timeout"));
        }, 30000);
      })]);
      requests.current.set(scope, request);
      try {
        const response = await request;
        const rows = response?.data?.data?.getClubInvoiceDetails;
        if (!Array.isArray(rows)) throw new Error("missing_invoice_details");
        if (active && version === invalidation.current) {
          hasRows = rows.length > 0;
          delay = hasRows ? 30000 : Math.min(delay * 2, 300000);
          setState({ scope, rows, error: false });
        }
      } catch (failure) {
        const denied = [401, 403].includes(failure?.statusCode || failure?.response?.status) ||
          failure?.codes?.some(code => ["FORBIDDEN_BILLING_ACCESS", "FORBIDDEN_CLUB_ACCESS", "UNAUTHORIZED_CLUB_ACCESS",
            "BILLING_ROLLOUT_DISABLED", "UNAUTHENTICATED", "UNAUTHORIZED", "FORBIDDEN"].includes(code));
        if (active && version === invalidation.current) {
          if (denied) hasRows = false;
          delay = hasRows ? 30000 : Math.min(delay * 2, 300000);
          setState(previous => ({ scope,
            rows: !denied && previous?.scope === scope ? previous.rows : [], error: true }));
        }
      } finally {
        clearTimeout(deadline);
        controller.signal.removeEventListener("abort", cancelRequest);
        controllers.current.delete(controller);
        if (requests.current.get(scope) === request) requests.current.delete(scope);
        pending = false;
        if (active) timer = setTimeout(load, delay);
      }
    };
    load();
    return () => { active = false; clearTimeout(timer); };
  }, [clubId, userId, scope, refresh]);
  return { ...(!clubId || !userId ? { rows: [], noScope: true } : state?.scope === scope ? state : { rows: [], loading: true }),
    refresh: () => setRefresh(value => value + 1),
    saved: invoiceId => {
      invalidation.current += 1;
      setState(previous => previous?.scope === scope ? { ...previous, error: false,
        rows: previous.rows.map(row => row.invoiceId === invoiceId
          ? { ...row, canEdit: false, reason: "processing" } : row) } : previous);
      setRefresh(value => value + 1);
    },
    invalidate: () => {
      invalidation.current += 1;
      setState({ scope, rows: [], error: true });
    } };
}

export function InvoiceDetailsNotice({ clubId, userId }) {
  const { t } = useTranslation();
  const state = useInvoiceDetails(clubId, userId);
  if (!state.rows.length) return null;
  const needsDetails = state.rows.some(row => row.canEdit === true);
  return <StatusAlert severity="info" className="mx-4 mt-6" title={t(needsDetails ? "billing_invoice_details_title" : "billing_invoice_status_title")}
    action={<Link to={`${BILLING_ROUTE}?invoice_details=1`} className={buttonVariants({ variant: "outline", size: "sm" })}>
      {t(needsDetails ? "billing_invoice_details_open" : "billing_invoice_status_open")}
    </Link>}>
    {t("billing_invoice_paid_pending")}
  </StatusAlert>;
}

export default function BillingInvoiceDetails() {
  const { authState } = useContext(AuthContext);
  const clubId = authState?.user?.club?._id ? String(authState.user.club._id) : null;
  const userId = authState?.user?._id ? String(authState.user._id) : null;
  const { t } = useTranslation();
  const state = useInvoiceDetails(clubId, userId);
  return <BillingPageLayout title={t("billing_invoice_status_title")} subtitle={t("billing_page_subtitle")}>
    {state.error && <StatusAlert severity="error">{t("billing_invoice_details_load_error")}</StatusAlert>}
    {state.noScope ? <StatusAlert severity="error">{t("your_account_does_not_belong_to_a_club")}</StatusAlert>
      : state.loading ? <StatusAlert severity="info">{t("loading")}</StatusAlert>
      : !state.rows.length ? state.error ? null : <StatusAlert severity="info">{t("billing_invoice_details_none")}</StatusAlert>
          : <>
            <p>{t("billing_invoice_paid_pending")}</p>
            {state.rows.map(row => <Card key={`${userId}:${clubId}:${row.invoiceId}`} size="sm">
              <CardHeader>
                <CardDescription className="break-all">{t("billing_invoice_reference")}: {row.invoiceId}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
              <StatusAlert severity={row.reason === "invalid_vat" ? "error" : "info"}>
                {t(row.reason === "invalid_vat" ? "billing_invoice_vat_invalid"
                  : row.reason === "review_required" ? "billing_invoice_details_review"
                  : row.reason === "verification_pending" ? "billing_invoice_vies_pending"
                    : row.canEdit ? "billing_invoice_details_needed" : "billing_invoice_processing")}
              </StatusAlert>
              {row.canEdit && <BillingTaxIdentityForm clubId={clubId} invoice={row} onSaved={() => state.saved(row.invoiceId)} onAccessDenied={state.invalidate} />}
              </CardContent>
            </Card>)}
          </>}
    <div className="flex flex-col items-start gap-2 border-t pt-4 sm:flex-row sm:flex-wrap sm:items-center">
      <Link to={`${BILLING_ROUTE}?billing_details=1`} className={buttonVariants({ variant: "outline", className: "h-auto min-h-9 whitespace-normal py-2 text-left" })}>{t("billing_invoice_future_details")}</Link>
      <Button type="button" variant="outline" onClick={state.refresh}>{t("retry")}</Button>
      <Link to={MANAGER_HOME_ROUTE} className={buttonVariants({ variant: "ghost", className: "h-auto min-h-9 whitespace-normal py-2 text-left" })}>{t("billing_back_to_manager")}</Link>
    </div>
  </BillingPageLayout>;
}
